import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type MenuItemsCollection = Record<string, string>;

type MenuEntry = {
  label: string;
  sectionId: string;
};

@Component({
  standalone: true,
  selector: 'app-section-menu',
  imports: [CommonModule],
  templateUrl: './section-menu.component.html',
  styleUrl: './section-menu.component.scss',
})
export class SectionMenuComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() menuItems: MenuItemsCollection = {};
  @Input() scrollContainerId = 'content';
  @Input() scrollOffset = 160;

  protected activeSectionId: string | null = null;
  protected menuEntries: MenuEntry[] = [];

  private readonly hostElement = inject(ElementRef<HTMLElement>);

  private scrollContainer: HTMLElement | null = null;
  private sectionElements: HTMLElement[] = [];
  private mutationObserver?: MutationObserver;
  private isProgrammaticScroll = false;
  private programmaticScrollTimeoutId?: number;
  private scrollRafId?: number;
  private initTimeoutId?: number;
  private hasViewInitialized = false;

  private readonly handleContainerScroll = (): void => {
    if (this.scrollRafId != null) {
      return;
    }

    this.scrollRafId = window.requestAnimationFrame(() => {
      this.scrollRafId = undefined;

      if (this.isProgrammaticScroll) {
        return;
      }

      this.updateActiveSectionFromScroll();
    });
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['menuItems']) {
      this.menuEntries = Object.entries(this.menuItems ?? {}).map(([label, sectionId]) => ({ label, sectionId }));
    }

    if (!this.hasViewInitialized) {
      return;
    }

    if (changes['menuItems'] || changes['scrollContainerId'] || changes['scrollOffset']) {
      this.setActiveSection(null);
      this.scheduleInitializeObservers();
    }
  }

  public ngAfterViewInit(): void {
    this.hasViewInitialized = true;
    this.scheduleInitializeObservers();
  }

  public ngOnDestroy(): void {
    this.teardownObservers();

    if (this.programmaticScrollTimeoutId != null) {
      window.clearTimeout(this.programmaticScrollTimeoutId);
    }

    if (this.initTimeoutId != null) {
      window.clearTimeout(this.initTimeoutId);
    }

    if (this.scrollRafId != null) {
      window.cancelAnimationFrame(this.scrollRafId);
    }
  }

  protected onMenuItemClick(sectionId: string): void {
    this.setActiveSection(sectionId, true);
    this.scrollToSection(sectionId);
  }

  protected trackBySectionId(_index: number, menuEntry: MenuEntry): string {
    return menuEntry.sectionId;
  }

  private initializeObservers(): void {
    this.teardownObservers();
    this.resolveScrollContainer();

    if (!this.scrollContainer) {
      this.sectionElements = [];
      this.setActiveSection(null);
      return;
    }

    this.scrollContainer.addEventListener('scroll', this.handleContainerScroll, { passive: true });

    this.refreshSectionElements();
    this.updateActiveSectionFromScroll();

    this.mutationObserver = new MutationObserver(() => {
      this.refreshSectionElements();
      this.updateActiveSectionFromScroll();
    });

    this.mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  private scheduleInitializeObservers(): void {
    if (this.initTimeoutId != null) {
      window.clearTimeout(this.initTimeoutId);
    }

    this.initTimeoutId = window.setTimeout(() => {
      this.initializeObservers();
    });
  }

  private teardownObservers(): void {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handleContainerScroll);
    }

    this.mutationObserver?.disconnect();
    this.mutationObserver = undefined;
    this.scrollContainer = null;
  }

  private resolveScrollContainer(): void {
    this.scrollContainer = document.getElementById(this.scrollContainerId);
  }

  private refreshSectionElements(): void {
    if (!this.scrollContainer) {
      this.sectionElements = [];
      return;
    }

    const entries = this.menuEntries
      .map((menuEntry) => document.getElementById(menuEntry.sectionId))
      .filter((section): section is HTMLElement => !!section)
      .sort((sectionA, sectionB) => this.getSectionTop(sectionA) - this.getSectionTop(sectionB));

    this.sectionElements = entries;
  }

  private scrollToSection(sectionId: string): void {
    this.resolveScrollContainer();

    if (!this.scrollContainer) {
      return;
    }

    const targetSection = document.getElementById(sectionId);
    if (!targetSection) {
      return;
    }

    this.isProgrammaticScroll = true;

    if (this.programmaticScrollTimeoutId != null) {
      window.clearTimeout(this.programmaticScrollTimeoutId);
    }

    const targetPosition = Math.max(this.getSectionTop(targetSection) - this.scrollOffset, 0);
    this.scrollContainer.scrollTo({ top: targetPosition, behavior: 'smooth' });

    this.programmaticScrollTimeoutId = window.setTimeout(() => {
      this.isProgrammaticScroll = false;
      this.updateActiveSectionFromScroll();
    }, 700);
  }

  private updateActiveSectionFromScroll(): void {
    if (!this.scrollContainer || this.sectionElements.length === 0) {
      this.setActiveSection(null);
      return;
    }

    const isNearBottom =
      this.scrollContainer.scrollTop + this.scrollContainer.clientHeight >= this.scrollContainer.scrollHeight - 2;

    if (isNearBottom) {
      this.setActiveSection(this.sectionElements[this.sectionElements.length - 1]?.id ?? null);
      return;
    }

    const sectionMarker = this.scrollContainer.scrollTop + this.scrollOffset + 1;
    let currentSection = this.sectionElements[0]?.id ?? null;

    for (const sectionElement of this.sectionElements) {
      if (this.getSectionTop(sectionElement) <= sectionMarker) {
        currentSection = sectionElement.id;
        continue;
      }

      break;
    }

    this.setActiveSection(currentSection);
  }

  private setActiveSection(sectionId: string | null, scrollIntoView = false): void {
    if (this.activeSectionId === sectionId) {
      return;
    }

    this.activeSectionId = sectionId;
    this.applyActiveMenuState();

    if (scrollIntoView && sectionId) {
      this.scrollMenuItemIntoView(sectionId);
    }
  }

  private applyActiveMenuState(): void {
    const menuItems = this.getMenuItems();

    for (const menuItem of menuItems) {
      const isActive = menuItem.dataset['sectionId'] === this.activeSectionId;
      menuItem.classList.toggle('active', isActive);

      if (isActive) {
        menuItem.setAttribute('aria-current', 'true');
      } else {
        menuItem.removeAttribute('aria-current');
      }
    }
  }

  private scrollMenuItemIntoView(sectionId: string): void {
    const menuItems = this.getMenuItems();
    const activeItem = Array.from(menuItems).find((menuItem) => menuItem.dataset['sectionId'] === sectionId);

    if (!activeItem || typeof activeItem.scrollIntoView !== 'function') {
      return;
    }

    activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  private getSectionTop(sectionElement: HTMLElement): number {
    if (!this.scrollContainer) {
      return sectionElement.offsetTop;
    }

    const sectionRect = sectionElement.getBoundingClientRect();
    const containerRect = this.scrollContainer.getBoundingClientRect();

    return sectionRect.top - containerRect.top + this.scrollContainer.scrollTop;
  }

  private getMenuItems(): HTMLElement[] {
    const hostNativeElement = this.hostElement.nativeElement as HTMLElement;
    const menuItems = hostNativeElement.querySelectorAll('[data-section-id]');

    return Array.from(menuItems).filter((menuItem): menuItem is HTMLElement => menuItem instanceof HTMLElement);
  }
}
